import { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Camera, ImagePlus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { notify } from "@/lib/notify";
import type { GpsUploadSource } from "@/lib/gallery-upload-gps";
import { useTranslation } from "react-i18next";

export interface PendingPhoto {
  file: File;
  preview: string;
  source: GpsUploadSource;
}

interface MultiPhotoCaptureProps {
  /** Same order as `files` — drives GPS: camera = device location, library = EXIF only */
  onUpload: (files: File[], sources: GpsUploadSource[]) => Promise<void>;
  /** Optional limit on number of photos */
  maxPhotos?: number;
  /** Disabled state (e.g. no folder selected) */
  disabled?: boolean;
  /** Upload in progress */
  uploadBusy?: boolean;
  /** Legacy default label; used when `cameraLabel` / `chooseFilesLabel` are omitted */
  addPhotoLabel?: string;
  /** Opens camera (`capture="environment"`) */
  cameraLabel?: string;
  /** Opens file / photo library picker (no `capture`) */
  chooseFilesLabel?: string;
  uploadLabel?: string;
  uploadingLabel?: string;
  className?: string;
  /** Egyszeri feltöltés drag–drop-ból vagy külső forrásból (könyvtár forrás, EXIF GPS). */
  seedFiles?: File[] | null;
  onSeedFilesConsumed?: () => void;
  /**
   * Ha true: `onUpload` resolve után ürül a pending (wizard: rövid mappa-előkészítés + háttérfeltöltés).
   * Ha false (alap): a teljes `onUpload` futása után ürül — megléző galéria viselkedés.
   */
  releasePendingImmediately?: boolean;
}

export function MultiPhotoCapture({
  onUpload,
  maxPhotos = 50,
  disabled = false,
  uploadBusy = false,
  addPhotoLabel = "Fénykép készítése",
  cameraLabel,
  chooseFilesLabel,
  uploadLabel = "Összes feltöltése",
  uploadingLabel = "Feltöltés...",
  className,
  seedFiles,
  onSeedFilesConsumed,
  releasePendingImmediately = false,
}: MultiPhotoCaptureProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingRef = useRef<PendingPhoto[]>([]);
  pendingRef.current = pending;

  const cameraBtnLabel = cameraLabel ?? addPhotoLabel;
  const filesBtnLabel = chooseFilesLabel ?? addPhotoLabel;

  useEffect(() => {
    return () => {
      pendingRef.current.forEach((p) => URL.revokeObjectURL(p.preview));
    };
  }, []);

  const fileLooksLikeImage = useCallback((file: File) => {
    if (file.type.startsWith("image/")) return true;
    return /\.(heic|heif)$/i.test(file.name);
  }, []);

  useEffect(() => {
    if (!seedFiles?.length) return;
    setPending((prev) => {
      const toAdd: PendingPhoto[] = [];
      for (const file of seedFiles) {
        if (prev.length + toAdd.length >= maxPhotos) break;
        if (!fileLooksLikeImage(file)) continue;
        toAdd.push({ file, preview: URL.createObjectURL(file), source: "library" });
      }
      if (toAdd.length === 0) {
        queueMicrotask(() => onSeedFilesConsumed?.());
        return prev;
      }
      queueMicrotask(() => onSeedFilesConsumed?.());
      return [...prev, ...toAdd].slice(0, maxPhotos);
    });
  }, [seedFiles, maxPhotos, fileLooksLikeImage, onSeedFilesConsumed]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, source: GpsUploadSource) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newList: PendingPhoto[] = [];
    let rejected = 0;
    for (let i = 0; i < files.length && pending.length + newList.length < maxPhotos; i++) {
      const file = files[i];
      if (!fileLooksLikeImage(file)) {
        rejected += 1;
        continue;
      }
      newList.push({ file, preview: URL.createObjectURL(file), source });
    }
    if (newList.length === 0 && rejected > 0) {
      notify.info(t("images.pickerNoImagesRecognized"), t("images.pickerNoImagesRecognizedDesc"));
    }
    setPending((prev) => [...prev, ...newList].slice(0, maxPhotos));
    e.target.value = "";
  };

  const removeAt = (index: number) => {
    setPending((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].preview);
      next.splice(index, 1);
      return next;
    });
  };

  const handleUpload = async () => {
    if (pending.length === 0) return;
    const files = pending.map((p) => p.file);
    const sources = pending.map((p) => p.source);
    if (releasePendingImmediately) {
      try {
        await onUpload(files, sources);
      } catch (error) {
        // Keep pending photos so the user can retry; never fail silently.
        notify.error(
          t("images.uploadFailedTitle", { defaultValue: "Feltöltés sikertelen" }),
          error instanceof Error ? error.message : undefined,
        );
        return;
      }
      setPending((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.preview));
        return [];
      });
      return;
    }
    await onUpload(files, sources);
    setPending((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.preview));
      return [];
    });
  };

  const pickDisabled = disabled || uploadBusy || pending.length >= maxPhotos;

  return (
    <div className={cn("space-y-4", className)}>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => handleInputChange(e, "camera")}
        disabled={pickDisabled}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleInputChange(e, "library")}
        disabled={pickDisabled}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="gap-2"
          disabled={pickDisabled}
          onClick={() => cameraInputRef.current?.click()}
        >
          <Camera className="h-5 w-5 shrink-0" />
          {cameraBtnLabel}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="gap-2"
          disabled={pickDisabled}
          onClick={() => fileInputRef.current?.click()}
        >
          <ImagePlus className="h-5 w-5 shrink-0" />
          {filesBtnLabel}
        </Button>
        {pending.length > 0 && (
          <Button
            type="button"
            size="lg"
            disabled={uploadBusy}
            onClick={handleUpload}
          >
            {uploadBusy ? uploadingLabel : `${uploadLabel} (${pending.length})`}
          </Button>
        )}
      </div>

      {pending.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
          {pending.map((item, index) => (
            <div
              key={item.preview + index}
              className="relative aspect-square rounded-lg overflow-hidden border bg-muted group"
            >
              <img
                src={item.preview}
                alt=""
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-90 hover:opacity-100 shadow"
                onClick={() => removeAt(index)}
                aria-label="Eltávolítás"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
